import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Gem, Sparkles, Check } from 'lucide-react';
import { useState } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { FEATURE_LABELS, PLANS, minPlanForFeature } from '@/config/plans';
import { billingAPI } from '@/lib/api';
import { toast } from 'sonner';

/**
 * Modal shown when a Starter user clicks a locked Pro/Enterprise feature.
 *
 * Props:
 *  - open: boolean
 *  - onOpenChange: (open) => void
 *  - feature: feature key from FEATURE_LABELS (e.g. 'seo_articles')
 */
export function UpsellModal({ open, onOpenChange, feature }) {
  const { language } = useLanguage();
  const tf = (fr, en) => (language === 'fr' ? fr : en);
  const [loading, setLoading] = useState(false);

  if (!feature) return null;

  const requiredPlanId = minPlanForFeature(feature);
  const requiredPlan = PLANS[requiredPlanId];
  const featureLabel = FEATURE_LABELS[feature]?.[language] || feature;

  // Bullet list of what the required plan brings vs Starter
  const starterFeatures = PLANS.starter.features;
  const planFeatures = requiredPlan.features;
  const unlockedKeys = Object.keys(planFeatures).filter(
    (k) => planFeatures[k] && !starterFeatures[k]
  );

  // Trigger Stripe Checkout for the required plan. Enterprise self-serve
  // is intentionally NOT exposed via Checkout here (handled by sales).
  const handleUpgrade = async () => {
    if (requiredPlanId === 'enterprise') {
      onOpenChange(false);
      return;
    }
    setLoading(true);
    try {
      const { data } = await billingAPI.createCheckout({ plan_id: requiredPlanId, interval: 'monthly' });
      if (data && data.checkout_url) {
        window.location.href = data.checkout_url;
        return;
      }
      toast.error(tf('Erreur paiement.', 'Payment error.'));
    } catch (e) {
      toast.error(e?.response?.data?.detail || tf('Erreur paiement.', 'Payment error.'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="upsell-modal">
        <DialogHeader>
          <div className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-br from-amber-400 to-orange-500 text-white shadow-lg">
            <Gem className="h-6 w-6" />
          </div>
          <DialogTitle className="text-center text-2xl">
            {tf('Fonctionnalite verrouillee', 'Locked feature')}
          </DialogTitle>
          <DialogDescription className="text-center">
            {tf(
              `"${featureLabel}" n'est pas inclus dans votre forfait actuel.`,
              `"${featureLabel}" is not included in your current plan.`
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="rounded-xl border-2 border-amber-200 bg-gradient-to-br from-amber-50 to-orange-50 p-5 space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-amber-600" />
              <span className="font-bold text-lg">
                {tf('Forfait', 'Plan')} {requiredPlan.name}
              </span>
            </div>
            <Badge className="bg-amber-500 hover:bg-amber-600 text-white" data-testid="upsell-required-plan">
              {requiredPlan.price_monthly === 0
                ? tf('Gratuit', 'Free')
                : `${requiredPlan.price_monthly}$ ${requiredPlan.currency}/mois`}
            </Badge>
          </div>

          {unlockedKeys.length > 0 && (
            <ul className="space-y-1.5 text-sm">
              {unlockedKeys.slice(0, 5).map((k) => (
                <li key={k} className="flex items-start gap-2">
                  <Check className="h-4 w-4 text-green-600 mt-0.5 shrink-0" />
                  <span>{FEATURE_LABELS[k]?.[language] || k}</span>
                </li>
              ))}
            </ul>
          )}
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="w-full sm:w-auto"
            data-testid="upsell-cancel-btn"
          >
            {tf('Plus tard', 'Later')}
          </Button>
          <Button
            onClick={handleUpgrade}
            disabled={loading}
            className="w-full sm:flex-1 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white"
            data-testid="upsell-upgrade-btn"
          >
            <Gem className="mr-2 h-4 w-4" />
            {loading
              ? tf('Chargement…', 'Loading…')
              : tf(`Passer au forfait ${requiredPlan.name}`, `Upgrade to ${requiredPlan.name}`)}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
